import { AUTOMATION_NODE_TYPE, BRANCH_KEY } from '@constants/automations';

export const NODE_WIDTH = 280;
export const NODE_HEIGHT = 76;
export const V_GAP = 56;

const H_GAP = 64;
const ROW = NODE_HEIGHT + V_GAP;
const MARGIN_X = 40;
const MARGIN_Y = 24;

const { BRANCH, TRIGGER, EXIT } = AUTOMATION_NODE_TYPE;

export function layoutFlow(nodes, edges) {
    const byId = new Map(nodes.map(node => [node.id, node]));
    const outgoing = new Map(nodes.map(node => [node.id, []]));
    edges.forEach(edge => outgoing.get(edge.source)?.push(edge));

    const edgesFrom = id => outgoing.get(id) || [];
    const isBranch = id => byId.get(id)?.data.type === BRANCH;
    const next = id => edgesFrom(id).find(e => !e.data?.branchKey)?.target;
    const branchEdge = (id, key) => edgesFrom(id).find(e => e.data?.branchKey === key);
    const branchTarget = (id, key) => branchEdge(id, key)?.target;

    const reachableMemo = new Map();
    const reachable = id => {
        let set = reachableMemo.get(id);
        if (!set) {
            set = new Set([id]);
            reachableMemo.set(id, set);
            edgesFrom(id).forEach(edge => reachable(edge.target).forEach(r => set.add(r)));
        }
        return set;
    };

    const mergeMemo = new Map();
    const mergeOf = branchId => {
        let merge = mergeMemo.get(branchId);
        if (merge === undefined) {
            const noReach = reachable(branchTarget(branchId, BRANCH_KEY.NO));
            const seen = new Set();
            merge = branchTarget(branchId, BRANCH_KEY.YES);
            while (merge && !noReach.has(merge) && !seen.has(merge)) {
                seen.add(merge);
                merge = isBranch(merge) ? mergeOf(merge) : next(merge);
            }
            mergeMemo.set(branchId, merge);
        }
        return merge;
    };

    const laneNodes = (startId, stopId) => {
        const lane = [];
        const seen = new Set();
        for (
            let cur = startId;
            cur && cur !== stopId && !seen.has(cur);
            cur = isBranch(cur) ? mergeOf(cur) : next(cur)
        ) {
            seen.add(cur);
            lane.push(cur);
        }
        return lane;
    };

    const laneWidth = (startId, stopId) =>
        laneNodes(startId, stopId).reduce((width, id) => {
            if (!isBranch(id)) return width;
            const merge = mergeOf(id);
            const arms =
                laneWidth(branchTarget(id, BRANCH_KEY.YES), merge) +
                H_GAP +
                laneWidth(branchTarget(id, BRANCH_KEY.NO), merge);
            return Math.max(width, arms);
        }, NODE_WIDTH);

    const positions = new Map();
    const edgeLanes = {};

    const layoutLane = (startId, stopId, centerX, y) => {
        for (const id of laneNodes(startId, stopId)) {
            positions.set(id, { x: centerX - NODE_WIDTH / 2, y });
            if (!isBranch(id)) {
                y += ROW;
                continue;
            }
            const merge = mergeOf(id);
            const yesEdge = branchEdge(id, BRANCH_KEY.YES);
            const noEdge = branchEdge(id, BRANCH_KEY.NO);
            const yesWidth = laneWidth(yesEdge?.target, merge);
            const noWidth = laneWidth(noEdge?.target, merge);
            const total = yesWidth + H_GAP + noWidth;
            const yesX = centerX - total / 2 + yesWidth / 2;
            const noX = centerX + total / 2 - noWidth / 2;
            const mergeY = Math.max(
                layoutLane(yesEdge?.target, merge, yesX, y + ROW),
                layoutLane(noEdge?.target, merge, noX, y + ROW),
            );
            const armSpansRows = merge && mergeY - y > ROW;
            if (armSpansRows && yesEdge?.target === merge) edgeLanes[yesEdge.id] = yesX;
            if (armSpansRows && noEdge?.target === merge) edgeLanes[noEdge.id] = noX;
            y = mergeY;
        }
        return y;
    };

    const trigger = nodes.find(node => node.data.type === TRIGGER);
    const exit = nodes.find(node => node.data.type === EXIT);
    const rootX = MARGIN_X + (trigger ? laneWidth(trigger.id, exit?.id) : NODE_WIDTH) / 2;

    let bottomY = trigger ? layoutLane(trigger.id, exit?.id, rootX, MARGIN_Y) : MARGIN_Y;
    for (const node of [exit, ...nodes]) {
        if (node && !positions.has(node.id)) {
            positions.set(node.id, { x: rootX - NODE_WIDTH / 2, y: bottomY });
            bottomY += ROW;
        }
    }

    return {
        nodes: nodes.map(node => ({ ...node, draggable: false, position: positions.get(node.id) })),
        edgeLanes,
    };
}
